import { Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useNotification } from '../../../hooks/NotificationContext';
import useDebounce from '../../../hooks/useDebounce';
import {
  isExecutiveManager,
  showUserDisplayName,
} from '../../../lib/userUtils';
import { createQueryString } from '../../../lib/utils';
import { getConfigurations, getTestCaseData } from '../../../modules/config';
import { getTestCases } from '../../../modules/jira';
import { getAssociatedUsers, getCurrentProject } from '../../../modules/project';
import { defaultTestCaseFilters } from '../../../static-data/defaultFilters';
import FilterButton from '../../elements/FilterButton';
import SearchBar from '../../elements/SearchBar';
import TestCasesTable from '../../elements/TableComponent';
import { testCaseTableHead } from '../test-case/testCaseTableHead';
import AddTestCaseModal from './AddTestCaseModal';
import TestCaseTableActions from './TestCaseTableActions';
import BulkEditModal from './bulk-edit/BulkEditModal';
import AppliedFilters from './filters/AppliedFilters';
import TestCaseFilterModal from './filters/TestCaseFilterModal';

const TestCasesTabContentComponent = () => {
  const [showModal, setShowModal] = useState(false);
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [modules, setModules] = useState([]);
  const [labels, setLabels] = useState([]);
  const [testSuites, setTestSuites] = useState([]);
  const [technologyLayers, setTechnologyLayers] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const { projectId } = useParams();
  const handleClose = () => setShowModal(false);
  const handleFilterClose = () => setShowFilterModal(false);
  const handleOpenTestCaseModal = () => setShowModal(true);
  const { addNotification } = useNotification();
  const [isReviewWorkflow, setIsReviewWorkflow] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState(
    location.state?.filters || defaultTestCaseFilters
  );
  const [searchValue, setSearchValue] = useState(
    location.state?.searchValue || ''
  );
  const [page, setPage] = useState(location.state?.page || 1);
  const debouncedSearchQuery = useDebounce(searchValue, 500);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const handleBulkEditModal = (value) => setIsBulkEditOpen(value);
  const [projectUsers, setProjectUsers] = useState([]);
  const excludeColumns = ['geographies', 'platforms'] ;
  const allColumnKeys = useMemo(() => {
  return testCaseTableHead
    .map(h => h.key)
    .filter(key => !excludeColumns.includes(key));
  }, [testCaseTableHead]);
  const STORAGE_KEY = `table-columns-testcases-${projectId}`;
  const [selectedColumns, setSelectedColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : allColumnKeys;
    } catch (error) {
      return allColumnKeys;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedColumns));
  }, [selectedColumns, STORAGE_KEY]);

  const fetchTestCases = async (
    currentPage = 1,
    searchValue = '',
    queryString = ''
  ) => {
    setLoading(true);
    try {
      const response = await getTestCases(
        showArchived,
        currentPage,
        perPage,
        searchValue,
        projectId,
        queryString
      );
      setTestCases(response?.testcases);
      setTotalCount(response?.total);
    } catch (error) {
      const errorMessage = error?.message || 'Failed to fetch test cases.';
      addNotification('danger', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectData = async () => {
    const response = await getCurrentProject(projectId);
    setIsReviewWorkflow(response.review_workflow);
  };

  const fetchTestCaseData = async () => {
    setLoading(true);
    try {
      const usersResponse = await getAssociatedUsers(projectId);
      const response = await getTestCaseData(projectId);
      if (response) {
        const fetchedStatuses = response?.result.filter(
          (item) => item.group === 'TEST_CASE_STATUS'
        );
        const fetchedPriorities = response?.result.filter(
          (item) => item.group === 'TEST_CASE_PRIORITY'
        );
        const fetchedLabels = response?.result.filter(
          (item) => item.group === 'LABEL'
        );
        const fetchedModules = response?.result.filter(
          (item) => item.group === 'FUNCTIONAL_MODULE_FIELD'
        );
        const fetchedTechnologyLayers = response?.result.filter(
          (item) => item.group === 'TECHNOLOGY_LAYER'
        );
        if (usersResponse) {
          setProjectUsers(
            usersResponse.map((userData) => ({
              ...userData.user,
              role: userData.role,
            }))
          );
        }
        const testSuitesResponse = response?.test_suites;
        setTestSuites(testSuitesResponse);

        setStatuses(fetchedStatuses);
        setPriorities(fetchedPriorities);
        setLabels(fetchedLabels);
        setModules(fetchedModules);
        setTechnologyLayers(fetchedTechnologyLayers);
      }
    } catch (error) {
      console.error('Error fetching test case data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const labelResponse = await getConfigurations('labels', projectId);
      setLabels(labelResponse?.data || []);
    } catch (error) {
      console.log('Failed to fetch data.');
    }
  };

  useEffect(() => {
    const queryString = createQueryString(filters);
    fetchTestCases(page, searchValue, queryString);
    navigate('.', {
      replace: true,
      state: { filters: filters, searchValue, page },
    });
  }, [showArchived, debouncedSearchQuery, filters, perPage]);

  useEffect(() => {
    const queryString = createQueryString(filters);
    fetchTestCases(page, searchValue, queryString);
  }, [page]);

  useEffect(() => {
    fetchTestCaseData();
    fetchProjectData();
  }, []);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    navigate('.', {
      replace: true,
      state: { filters, searchValue, page: newPage },
    });
  };

  const handlePageSizeChange = (e) => {
    const newPerPage = parseInt(e.target.value, 10);
    setPerPage(newPerPage);
    setPage(1);
    navigate('.', {
        replace: true,
        state: { filters, searchValue, page: 1 },
    });
  };

  const formatedTestCases = useMemo(() => {
    return testCases.map((testCase) => {
      return {
        ...testCase,
        owner: showUserDisplayName(testCase.owner, projectUsers),
        label:
          testCase.labels && testCase.labels.length > 0
            ? testCase.labels
                ?.map((label) => {
                  const exLabel =
                    labels && labels.find((l) => l.id === label)?.name;
                  return exLabel;
                })
                ?.join(', ')
            : '-',
        module: testCase.module?.name || '-',
        statusName: testCase.status?.name || '-',
        priorityName: testCase.priority?.name || '-',
        testSuites: testCase?.test_suites && testCase?.test_suites?.length > 0 ? 
        testCase.test_suites
          .map((testSuite) => {
            return testSuites?.find((suite) => suite.id === testSuite)?.name || '-'
          })
          .join(', ') : '-',
      };
    });
  }, [testCases, projectUsers, labels]);

  const refreshData = () => {
    setSelected([]);
    const queryString = createQueryString(filters);
    fetchTestCases(page, searchValue, queryString);
  };

  const handleShowArchived = () => {
    setShowArchived(!showArchived);
    handlePageChange(1);
    setSelected([]);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    setPage(1);
  };

  return (
    <Container fluid className="mt-2">
      <Row>
        <Col>
          <Typography variant="h4" className="mb-3">
            Test Cases
          </Typography>
        </Col>
      </Row>
      <Row className="align-items-center">
        <Col className="d-flex" md="4">
          <SearchBar
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            clearSearch={() => handleSearch('')}
            placeholder="Search Test Cases"
          />
          <FilterButton onClick={() => setShowFilterModal(true)} />
        </Col>
        {!isExecutiveManager() && (
          <Col className="d-flex justify-content-end">
            <TestCaseTableActions
              projectId={projectId}
              handleOpenTestCaseModal={handleOpenTestCaseModal}
              isSelectedItems={selected.length}
              seletectItems={selected}
              showArchived={showArchived}
              refreshData={refreshData}
              selectedRows={selectedRows}
              setShowArchived={handleShowArchived}
              handleBulkEditModal={handleBulkEditModal}
              setSelected={setSelected}
              fetchTestCaseData={fetchTestCaseData}
            />
          </Col>
        )}
      </Row>
      <Row className="mt-3">
        <Col>
          <AppliedFilters
            filters={filters}
            statuses={statuses}
            priorities={priorities}
            labels={labels}
            modules={modules}
            setFilters={setFilters}
            projectUsers={projectUsers}
            projectId={projectId}
            testSuites={testSuites}
          />
        </Col>
      </Row>
      <TestCasesTable
        data={formatedTestCases || []}
        headerTabs={testCaseTableHead}
        showCheckbox={isExecutiveManager() ? false : true}
        page={page}
        perPage={perPage}
        setPerPage={handlePageSizeChange}
        order={order}
        setOrder={setOrder}
        defaultSort="created_at"
        selected={selected}
        isLoading={loading}
        striped
        setSelectedRows={setSelectedRows}
        setSelected={setSelected}
        totalCount={totalCount}
        showPages
        handlePageChange={handlePageChange}
        tablekey="test cases"
        showCount={true}
        showRowCount={true}
        showColumnManager={true}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        mandatoryKeys={['key', 'summary']}
      />

      <AddTestCaseModal
        showModal={showModal}
        handleClose={handleClose}
        fetchTestCases={fetchTestCases}
        projectUsers={projectUsers || []}
        statuses={statuses}
        priorities={priorities}
        testSuites={testSuites}
        projectId={projectId}
        labels={labels}
        modules={modules}
        loading={loading}
        isReviewWorkflow={isReviewWorkflow}
        refetchData={fetchTestCaseData}
      />
      <TestCaseFilterModal
        showModal={showFilterModal}
        handleClose={handleFilterClose}
        projectUsers={projectUsers}
        statuses={statuses?.filter((status) => status?.name !== 'Archived')}
        priorities={priorities}
        labels={labels}
        modules={modules}
        loading={loading}
        setFilters={setFilters}
        filters={filters}
        setPage={setPage}
        testSuites={testSuites}
      />
      <BulkEditModal
        isOpen={isBulkEditOpen}
        onClose={handleBulkEditModal}
        selectedTestCases={selectedRows}
        priorities={priorities}
        statuses={statuses}
        labels={labels}
        modules={modules}
        testSuites={testSuites}
        loading={loading}
        isReviewWorkflow={isReviewWorkflow}
        projectUsers={projectUsers}
        technologyLayers={technologyLayers}
        fetchTestCases={refreshData}
        fetchDropdownData={fetchDropdownData}
      />
    </Container>
  );
};

export default TestCasesTabContentComponent;
